import {
  ProfessionalAuditReportFindingsSection,
  ProfessionalAuditReportRecommendationsSection
} from '@mieterengel/database-types'
import axios from 'axios'
import { AuditReportPayload } from 'lawyer-manager'
import { all, call, getContext, put, select, takeEvery } from 'redux-saga/effects'

import { delay } from '@app/common/helpers/delay'
import { logger } from '@app/common/logger/logger'
import { parseFileExtension } from '@app/lib/mime'
import { requireAuthToken } from '@app/modules/Auth/resolveAuthToken'
import { AuditReportData } from '@app/modules/Messages/store/models'
import { AddScheduledActionDocument } from '@app/queries/AddScheduledAction/AddScheduledAction.queries'
import { AnswerLetterRequestDocument } from '@app/queries/AnswerLetterRequest/AnswerLetterRequest.queries'
import { AttachTemplateToMessageDocument } from '@app/queries/AttachTemplateToMessage/AttachTemplateToMessage.queries'
import { CustomerAsLawyerDocument } from '@app/queries/CustomerAsLawyer/CustomerAsLawyer.queries'
import { LawyerDocumentsAsLawyerDocument } from '@app/queries/LawyerDocumentsAsLawyer/LawyerDocumentsAsLawyer.queries'
import { LetterRequestsForCustomerDocument } from '@app/queries/LetterRequestsForCustomer/LetterRequestsForCustomer.queries'
import { NavBarCountersDocument } from '@app/queries/NavBar/NavBarCounters.queries'
import { PostponedLetterRequestsDocument } from '@app/queries/PostponedLetterRequests/PostponedLetterRequests.queries'
import SDK from '@app/SDK'

import LawyerChatAction, {
  AnswerAction,
  MessagesActionType,
  SubmitAuditReportAction,
  UploadDocumentAction
} from './actions'

function* refetchCustomer(customerId: number) {
  const client = yield getContext('graphQLClient')
  yield call(client.query, {
    query: CustomerAsLawyerDocument,
    variables: {
      customerId
    },
    fetchPolicy: 'network-only'
  })
}

function* refetchLetter(customerId: number) {
  const client = yield getContext('graphQLClient')
  yield call(client.query, {
    query: LetterRequestsForCustomerDocument,
    variables: {
      customerId
    },
    fetchPolicy: 'network-only'
  })
}

export function* refetchNavBarCounters() {
  const client = yield getContext('graphQLClient')
  const lawyerId = yield select((state: any) => state.auth.id)
  yield call(client.query, {
    query: NavBarCountersDocument,
    fetchPolicy: 'network-only',
    variables: {
      lawyerId
    }
  })
}

const answerWithoutReport = async (answerWithoutReportPayload): Promise<{ data: { data } }> => {
  const token = await requireAuthToken()
  return SDK.lawyerManager.answerWithoutReport(token, answerWithoutReportPayload)
}

function* lawyerAnswer({ meta }: AnswerAction) {
  const client = yield getContext('graphQLClient')

  const { data, latestMessageId, isInformation, templateId, customerId } = meta

  try {
    const selectedDocuments = yield select(
      (state: any) => state.lawyerChat.answerWithoutReport.selectedDocuments
    )
    const lawyerLetterId = yield select(
      (state: any) => state.lawyerChat.answerWithoutReport.selectedLetterId
    )
    const documents = lawyerLetterId ? [...selectedDocuments, lawyerLetterId] : selectedDocuments
    const letterRequestId = yield select((state: any) => state.lawyerChat.letterRequestId)

    const res = yield call(answerWithoutReport, {
      latestMessageId,
      lawyerAnswerData: { ...data, documents },
      isInformation
    })

    yield call(client.mutate, {
      mutation: AddScheduledActionDocument,
      variables: {
        input: {
          action: 'FollowUp.IsProblemSolved',
          customerId,
          dueDateInDays: 14
        }
      }
    })

    if (lawyerLetterId) {
      yield call(client.mutate, {
        mutation: AnswerLetterRequestDocument,
        variables: {
          letterRequestId,
          lawyerLetterId
        }
      })
      yield call(client.query, {
        query: PostponedLetterRequestsDocument
      })
    }

    if (templateId) {
      const client = yield getContext('graphQLClient')
      yield call(client.mutate, {
        mutation: AttachTemplateToMessageDocument,
        variables: {
          templateId,
          messageId: res.data.data.id
        }
      })
    }

    yield call(refetchCustomer, res.data.data.customer.id)
    yield call(refetchLetter, res.data.data.customer.id)
    yield call(refetchNavBarCounters)
    // Giving slow computers and internet connections an extra second to add new Answer to Chat before loader goes away:
    yield call(delay, 1000)
    yield put(LawyerChatAction.clearSelectedDocs())
    yield put(LawyerChatAction.lawyerAnswerSuccess(res.data.data))
  } catch (e) {
    logger.error(e)
    yield put(LawyerChatAction.lawyerAnswerError(e))
  }
}

function* getLawyerDocs() {
  try {
    const client = yield getContext('graphQLClient')
    const { data } = yield call(client.query, {
      query: LawyerDocumentsAsLawyerDocument,
      fetchPolicy: 'network-only'
    })

    yield put(LawyerChatAction.setLawyerDocuments(data.lawyerDocumentsAsLawyer))
  } catch (e) {
    logger.error(e)
    yield put(LawyerChatAction.lawyerDocumentsError(e))
  }
}

function* onLawyerDocUpload({ meta: { data, audit, letter } }: UploadDocumentAction) {
  try {
    const token = yield call(requireAuthToken)
    const fileName = data.get('name')
    const file = data.get('lawyerdocument')

    const { documentId, signedUrl, headers } = yield call(
      SDK.documentAnalyzer.authorizeLawyerUpload,
      {
        token,
        file: {
          fileExtension: parseFileExtension(file.name, file.type),
          fileName
        }
      }
    )

    yield call(axios.put, signedUrl, file, {
      headers: {
        'Content-Type': file.type,
        ...headers
      }
    })
    if (audit) {
      if (letter) {
        yield put(LawyerChatAction.onAddSelectedLetterToAudit(documentId))
      } else {
        yield put(LawyerChatAction.onAuditSelectedDocAdd(documentId))
      }
    } else {
      if (letter) {
        yield put(LawyerChatAction.onAddSelectedLetter(documentId))
      } else {
        yield put(LawyerChatAction.onSelectedDocAdd(documentId))
      }
    }

    yield getLawyerDocs()
  } catch (e) {
    logger.error(e)
    yield put(LawyerChatAction.onUploadDocError(e))
  }
}

const auditReport = async (auditReportData: AuditReportData): Promise<{ data: { data } }> => {
  const token = await requireAuthToken()
  const professionalAuditReportFindingsSection: ProfessionalAuditReportFindingsSection = {
    uid: 'AuditReport.Section.Findings',
    outcome: auditReportData.outcome,
    summary: auditReportData.findings || [],
    checks: []
  }
  const professionalAuditReportRecommendationsSection: ProfessionalAuditReportRecommendationsSection =
    {
      uid: 'AuditReport.Section.Recommendations',
      summary: auditReportData.recommendations || [],
      checks: []
    }

  const auditReportPayload: AuditReportPayload = {
    latestMessageId: auditReportData.latestMessageId,
    auditReport: {
      auditType: auditReportData.auditType,
      sections: [
        professionalAuditReportFindingsSection,
        professionalAuditReportRecommendationsSection
      ]
    },
    documents: auditReportData.documents
  }
  return SDK.lawyerManager.auditReport(token, auditReportPayload)
}

function* submitAuditReport({
  meta: { data, latestMessage, templateId, customerId }
}: SubmitAuditReportAction) {
  try {
    const client = yield getContext('graphQLClient')
    const documents = yield select((state: any) => state.lawyerChat.auditReport.selectedDocuments)
    const lawyerLetterId = yield select(
      (state: any) => state.lawyerChat.auditReport.selectedLetterId
    )
    const letterRequestId = yield select((state: any) => state.lawyerChat.letterRequestId)

    const res = yield call(auditReport, {
      outcome: data.outcome,
      findings: data.findings,
      recommendations: data.recommendations,
      auditType: data.auditType,
      latestMessageId: latestMessage.id,
      documents: lawyerLetterId ? [...documents, lawyerLetterId] : documents
    })

    yield put(LawyerChatAction.saveAuditData({}))

    yield call(client.mutate, {
      mutation: AddScheduledActionDocument,
      variables: {
        input: {
          action: 'FollowUp.IsProblemSolved',
          customerId,
          dueDateInDays: 14
        }
      }
    })

    if (lawyerLetterId) {
      yield call(client.mutate, {
        mutation: AnswerLetterRequestDocument,
        variables: {
          letterRequestId,
          lawyerLetterId
        }
      })
      yield call(client.query, {
        query: PostponedLetterRequestsDocument
      })
    }

    if (templateId) {
      const client = yield getContext('graphQLClient')
      yield call(client.mutate, {
        mutation: AttachTemplateToMessageDocument,
        variables: {
          templateId,
          messageId: res.data.data.id
        }
      })
    }

    yield call(refetchCustomer, res.data.data.customer.id)
    yield call(refetchLetter, res.data.data.customer.id)
    yield call(refetchNavBarCounters)
    // Giving slow computers and internet connections an extra second to add new Answer to Chat before loader goes away:
    yield call(delay, 1000)
    yield put(LawyerChatAction.clearSelectedDocs())
    yield put(LawyerChatAction.lawyerAnswerSuccess(res.data.data))
  } catch (e) {
    logger.error(e)
    yield put(LawyerChatAction.lawyerAnswerError(e))
  }
}

export default function* lawyerChatSaga() {
  yield all([
    takeEvery(MessagesActionType.ANSWER, lawyerAnswer),
    takeEvery(MessagesActionType.SUBMIT_AUDIT_REPORT, submitAuditReport),
    takeEvery(MessagesActionType.LAWYER_DOC_UPLOAD, onLawyerDocUpload)
  ])
}
